import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { TabBar, TabBtn } from '../../components/common/Tabs';
import { financeAPI, gateEntryAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  HiOutlineRefresh, HiOutlineX, HiOutlineLightningBolt,
  HiOutlineEye, HiOutlinePlus, HiOutlineOfficeBuilding,
  HiOutlineDocumentText, HiOutlineShoppingCart, HiOutlineTruck,
  HiOutlineDownload, HiOutlineCheckCircle, HiOutlineExclamationCircle,
  HiOutlinePencil, HiOutlineTrash, HiOutlineSearch, HiOutlineArchive,
  HiOutlineArrowDown, HiOutlineArrowUp, HiOutlineClipboardList
} from 'react-icons/hi';
import toast from 'react-hot-toast';

const FinanceDashboard = () => {
  const { user } = useAuth();

  // Active Tab: 'vendors' | 'quotations' | 'pos' | 'purchase_store' | 'gate_inward' | 'ocr'
  const [activeTab, setActiveTab] = useState('purchase_store');

  // Data States
  const [vendors, setVendors] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [pos, setPos] = useState([]);
  const [gateEntries, setGateEntries] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [storeInventory, setStoreInventory] = useState([]);
  const [storeLedger, setStoreLedger] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search & Filter
  const [searchQuery, setSearchQuery] = useState('');
  const [ledgerFilter, setLedgerFilter] = useState('ALL'); // 'ALL' | 'IN' | 'OUT'

  // Modals State
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [vendorForm, setVendorForm] = useState({
    vendorCode: '', vendorName: '', contactPerson: '', phone: '', email: '',
    address: '', city: '', state: '', gstNumber: '', panNumber: '',
    materialSupplied: 'Nylon Granules', paymentTerms: '30 Days Net', rating: 5, notes: ''
  });
  const [editingVendorId, setEditingVendorId] = useState(null);

  const [showQuotationModal, setShowQuotationModal] = useState(false);
  const [quotationForm, setQuotationForm] = useState({
    quotationNumber: '', vendorId: '', quotationDate: new Date().toISOString().slice(0, 10),
    validUntil: '', paymentTerms: '30 Days Net', deliveryLeadTime: '7 Days', remarks: '',
    items: [{ materialType: 'Nylon Granules', description: 'Raw Material', quantity: 500, unit: 'kg', unitRate: 180, discountPercent: 0, taxRate: 18 }]
  });

  const [showPOModal, setShowPOModal] = useState(false);
  const [poForm, setPoForm] = useState({
    poNumber: '', vendorId: '', quotationId: '', orderDate: new Date().toISOString().slice(0, 10),
    expectedDeliveryDate: '', paymentTerms: '30 Days Net', deliveryTerms: 'Door Delivery Factory Gate', notes: '',
    items: [{ materialType: 'Nylon Granules', description: 'Raw Material', quantity: 500, unit: 'kg', rate: 180, discountPercent: 0, gstRate: 18 }]
  });

  // Purchase Store Inward Modal State
  const [showInwardModal, setShowInwardModal] = useState(false);
  const [inwardForm, setInwardForm] = useState({
    materialName: 'Nylon 66 Granules', materialType: 'nylon', quantity: 500, unit: 'kg',
    storeLocation: 'Purchase Store - Main Bay', rackBin: 'Rack A-1',
    referenceType: 'PO_INWARD', poNumber: '', vendorName: '', billNumber: '', remarks: ''
  });

  // Purchase Store Outward / Issue Modal State
  const [showOutwardModal, setShowOutwardModal] = useState(false);
  const [outwardForm, setOutwardForm] = useState({
    materialId: '', materialName: '', quantity: 100, unit: 'kg',
    destinationDepartment: 'Molding Line 1', machineNumber: 'M-01',
    issuedToPerson: '', issueSlipNumber: '', remarks: ''
  });

  // Upload/Scan State
  const [selectedFile, setSelectedFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [selectedInvoiceForView, setSelectedInvoiceForView] = useState(null);

  // Fetch all procurement data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [vendorsRes, quotesRes, posRes, gateRes, invRes, storeInvRes, storeLedgerRes] = await Promise.all([
        financeAPI.getVendors().catch(() => ({ data: { data: [] } })),
        financeAPI.getQuotations().catch(() => ({ data: { data: [] } })),
        financeAPI.getPOs().catch(() => ({ data: { data: [] } })),
        gateEntryAPI.getAll({ entryType: 'inward', limit: 50 }).catch(() => ({ data: { data: [] } })),
        financeAPI.getInvoices().catch(() => ({ data: { data: [] } })),
        financeAPI.getStoreInventory().catch(() => ({ data: { data: [] } })),
        financeAPI.getStoreLedger().catch(() => ({ data: { data: [] } }))
      ]);

      setVendors(vendorsRes.data.data || []);
      setQuotations(quotesRes.data.data || []);
      setPos(posRes.data.data || []);
      setGateEntries(gateRes.data.data || []);
      setInvoices(invRes.data.data || []);
      setStoreInventory(storeInvRes.data.data || []);
      setStoreLedger(storeLedgerRes.data.data || []);
    } catch (err) {
      toast.error('Failed to load procurement and store data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ==========================================
  // VENDOR ACTIONS
  // ==========================================
  const handleOpenVendorModal = (vendor = null) => {
    if (vendor) {
      setEditingVendorId(vendor._id);
      setVendorForm({
        vendorCode: vendor.vendorCode || '',
        vendorName: vendor.vendorName || '',
        contactPerson: vendor.contactPerson || '',
        phone: vendor.phone || '',
        email: vendor.email || '',
        address: vendor.address || '',
        city: vendor.city || '',
        state: vendor.state || '',
        gstNumber: vendor.gstNumber || '',
        panNumber: vendor.panNumber || '',
        materialSupplied: vendor.materialSupplied || 'Nylon Granules',
        paymentTerms: vendor.paymentTerms || '30 Days Net',
        rating: vendor.rating || 5,
        notes: vendor.notes || ''
      });
    } else {
      setEditingVendorId(null);
      setVendorForm({
        vendorCode: '', vendorName: '', contactPerson: '', phone: '', email: '',
        address: '', city: '', state: '', gstNumber: '', panNumber: '',
        materialSupplied: 'Nylon Granules', paymentTerms: '30 Days Net', rating: 5, notes: ''
      });
    }
    setShowVendorModal(true);
  };

  const handleSaveVendor = async (e) => {
    e.preventDefault();
    try {
      if (editingVendorId) {
        await financeAPI.updateVendor(editingVendorId, vendorForm);
        toast.success('Vendor updated successfully');
      } else {
        const res = await financeAPI.addVendor(vendorForm);
        toast.success(res.data.message || 'Vendor created successfully');
      }
      setShowVendorModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save vendor');
    }
  };

  const handleDeleteVendor = async (id) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;
    try {
      await financeAPI.deleteVendor(id);
      toast.success('Vendor removed');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete vendor');
    }
  };

  // ==========================================
  // QUOTATION ACTIONS
  // ==========================================
  const handleOpenQuotationModal = () => {
    setQuotationForm({
      quotationNumber: '',
      vendorId: vendors[0]?._id || '',
      quotationDate: new Date().toISOString().slice(0, 10),
      validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      paymentTerms: '30 Days Net',
      deliveryLeadTime: '7 Days',
      remarks: '',
      items: [{ materialType: 'Nylon Granules', description: 'Raw Material Grade A', quantity: 500, unit: 'kg', unitRate: 180, discountPercent: 0, taxRate: 18 }]
    });
    setShowQuotationModal(true);
  };

  const handleQuotationItemChange = (idx, field, val) => {
    const updated = [...quotationForm.items];
    updated[idx][field] = val;
    setQuotationForm(p => ({ ...p, items: updated }));
  };

  const handleAddQuotationItem = () => {
    setQuotationForm(p => ({
      ...p,
      items: [...p.items, { materialType: 'Nylon Granules', description: '', quantity: 100, unit: 'kg', unitRate: 150, discountPercent: 0, taxRate: 18 }]
    }));
  };

  const handleRemoveQuotationItem = (idx) => {
    if (quotationForm.items.length === 1) return;
    setQuotationForm(p => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));
  };

  const handleSaveQuotation = async (e) => {
    e.preventDefault();
    try {
      const res = await financeAPI.createQuotation(quotationForm);
      toast.success(res.data.message || 'Quotation saved');
      setShowQuotationModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create quotation');
    }
  };

  const handleConvertToPO = async (quote) => {
    if (!window.confirm(`Generate official Purchase Order (PO) from Quotation ${quote.quotationNumber}?`)) return;
    try {
      const res = await financeAPI.convertQuotationToPO(quote._id);
      toast.success(res.data.message || 'PO generated successfully!');
      fetchData();
      setActiveTab('pos');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to convert quotation to PO');
    }
  };

  // ==========================================
  // PURCHASE ORDER (PO) ACTIONS
  // ==========================================
  const handleOpenPOModal = () => {
    setPoForm({
      poNumber: '',
      vendorId: vendors[0]?._id || '',
      quotationId: '',
      orderDate: new Date().toISOString().slice(0, 10),
      expectedDeliveryDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      paymentTerms: '30 Days Net',
      deliveryTerms: 'Door Delivery Factory Gate',
      notes: 'Material subject to inspection and quality testing at factory gate.',
      items: [{ materialType: 'Nylon Granules', description: 'Raw Material Grade A', quantity: 500, unit: 'kg', rate: 180, discountPercent: 0, gstRate: 18 }]
    });
    setShowPOModal(true);
  };

  const handlePOItemChange = (idx, field, val) => {
    const updated = [...poForm.items];
    updated[idx][field] = val;
    setPoForm(p => ({ ...p, items: updated }));
  };

  const handleAddPOItem = () => {
    setPoForm(p => ({
      ...p,
      items: [...p.items, { materialType: 'Nylon Granules', description: '', quantity: 100, unit: 'kg', rate: 150, discountPercent: 0, gstRate: 18 }]
    }));
  };

  const handleRemovePOItem = (idx) => {
    if (poForm.items.length === 1) return;
    setPoForm(p => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));
  };

  const handleSavePO = async (e) => {
    e.preventDefault();
    try {
      const res = await financeAPI.createPO(poForm);
      toast.success(res.data.message || 'Purchase Order issued');
      setShowPOModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to issue PO');
    }
  };

  const handleDownloadPOPDF = (po) => {
    const token = localStorage.getItem('token');
    const url = `/api/finance/pos/${po._id}/pdf?token=${token}`;
    window.open(url, '_blank');
  };

  // ==========================================
  // PURCHASE STORE INWARD / OUTWARD ACTIONS
  // ==========================================
  const handleOpenInwardModal = (prefill = null) => {
    if (prefill) {
      setInwardForm({
        materialName: prefill.materialName || prefill.name || 'Nylon 66 Granules',
        materialType: prefill.materialType || prefill.type || 'nylon',
        quantity: prefill.quantity || 100,
        unit: prefill.unit || 'kg',
        storeLocation: prefill.storeLocation || 'Purchase Store - Main Bay',
        rackBin: prefill.rackBin || prefill.rackNumber || 'Rack A-1',
        referenceType: prefill.poNumber ? 'PO_INWARD' : 'DIRECT_PURCHASE_INWARD',
        poNumber: prefill.poNumber || '',
        vendorName: prefill.vendorName || '',
        billNumber: prefill.billNumber || '',
        remarks: prefill.remarks || 'Inward placed by Purchase user'
      });
    } else {
      setInwardForm({
        materialName: 'Nylon 66 Granules',
        materialType: 'nylon',
        quantity: 500,
        unit: 'kg',
        storeLocation: 'Purchase Store - Main Bay',
        rackBin: 'Rack A-1',
        referenceType: 'PO_INWARD',
        poNumber: pos[0]?.poNumber || '',
        vendorName: pos[0]?.vendorName || '',
        billNumber: '',
        remarks: 'Direct Inward into Purchase Store'
      });
    }
    setShowInwardModal(true);
  };

  const handleSaveInward = async (e) => {
    e.preventDefault();
    try {
      const res = await financeAPI.placeStoreInward(inwardForm);
      toast.success(res.data.message || 'Inward stock added to Purchase Store!');
      setShowInwardModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to place inward entry');
    }
  };

  const handleOpenOutwardModal = (mat = null) => {
    if (mat) {
      setOutwardForm({
        materialId: mat._id,
        materialName: mat.name,
        quantity: Math.min(100, mat.currentStock || 10),
        unit: mat.unit || 'kg',
        destinationDepartment: 'Molding Line 1',
        machineNumber: 'M-01',
        issuedToPerson: '',
        issueSlipNumber: `ISSUE-${Date.now().toString().slice(-4)}`,
        remarks: 'Issued for production run'
      });
    } else {
      const first = storeInventory[0];
      setOutwardForm({
        materialId: first?._id || '',
        materialName: first?.name || '',
        quantity: 50,
        unit: first?.unit || 'kg',
        destinationDepartment: 'Molding Line 1',
        machineNumber: 'M-01',
        issuedToPerson: '',
        issueSlipNumber: `ISSUE-${Date.now().toString().slice(-4)}`,
        remarks: 'Issued for production run'
      });
    }
    setShowOutwardModal(true);
  };

  const handleSaveOutward = async (e) => {
    e.preventDefault();
    try {
      const res = await financeAPI.placeStoreOutward(outwardForm);
      toast.success(res.data.message || 'Stock issued successfully!');
      setShowOutwardModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to issue stock');
    }
  };

  // ==========================================
  // AI OCR INVOICE SCANNER
  // ==========================================
  const handleScanInvoice = async () => {
    if (!selectedFile) return toast.error('Please select an image or PDF bill');
    setScanning(true);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const res = await financeAPI.scanInvoice(formData);
      toast.success(`OCR Success: Bill from ${res.data.aiExtracted.vendor_name}`);
      setSelectedFile(null);
      fetchData();
    } catch (err) {
      toast.error('AI Scan failed');
    } finally {
      setScanning(false);
    }
  };

  // Metrics Calculation
  const totalPOVolume = pos.reduce((acc, p) => acc + (p.grandTotal || 0), 0);
  const totalStoreStockKg = storeInventory.reduce((acc, it) => acc + (it.currentStock || 0), 0);
  const totalInwardKg = storeInventory.reduce((acc, it) => acc + (it.totalInward || 0), 0);
  const totalIssuedKg = storeInventory.reduce((acc, it) => acc + (it.totalIssued || 0), 0);

  return (
    <DashboardLayout pageTitle="Procurement & Purchase Hub">

      {/* TOP KPI STATS SUMMARY BAR */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '14px', marginBottom: '20px'
      }}>
        <div className="glass-card" style={{ padding: '14px 18px', borderLeft: '4px solid #3b82f6' }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', textTransform: 'uppercase', fontWeight: 700 }}>Total Vendors</div>
          <div style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--text-primary)', marginTop: '2px' }}>{vendors.length}</div>
          <div style={{ fontSize: '0.7rem', color: '#3b82f6', marginTop: '2px' }}>🏭 Registered Suppliers</div>
        </div>

        <div className="glass-card" style={{ padding: '14px 18px', borderLeft: '4px solid #10b981' }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', textTransform: 'uppercase', fontWeight: 700 }}>Store Raw Materials</div>
          <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#10b981', marginTop: '2px' }}>{storeInventory.length} Types</div>
          <div style={{ fontSize: '0.7rem', color: '#10b981', marginTop: '2px' }}>📦 {totalStoreStockKg.toLocaleString()} kg In Stock</div>
        </div>

        <div className="glass-card" style={{ padding: '14px 18px', borderLeft: '4px solid #22c55e' }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', textTransform: 'uppercase', fontWeight: 700 }}>Total Inward Received</div>
          <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#22c55e', marginTop: '2px' }}>{totalInwardKg.toLocaleString()} kg</div>
          <div style={{ fontSize: '0.7rem', color: '#22c55e', marginTop: '2px' }}>📥 Inward into Purchase Store</div>
        </div>

        <div className="glass-card" style={{ padding: '14px 18px', borderLeft: '4px solid #f97316' }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', textTransform: 'uppercase', fontWeight: 700 }}>Total Issued Outward</div>
          <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#f97316', marginTop: '2px' }}>{totalIssuedKg.toLocaleString()} kg</div>
          <div style={{ fontSize: '0.7rem', color: '#f97316', marginTop: '2px' }}>📤 Issued to Production</div>
        </div>

        <div className="glass-card" style={{ padding: '14px 18px', borderLeft: '4px solid #a855f7' }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', textTransform: 'uppercase', fontWeight: 700 }}>Procurement PO Value</div>
          <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#a855f7', marginTop: '2px' }}>₹{totalPOVolume.toLocaleString()}</div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', marginTop: '2px' }}>🛒 {pos.length} Purchase Orders</div>
        </div>
      </div>

      {/* WORKSPACE NAVIGATION TABS (actions for the open tab sit on their own line below) */}
      <div style={{ marginBottom: '16px' }}>
        <TabBar style={{ marginBottom: '10px' }}>
          {[
            { key: 'purchase_store', label: 'Purchase Store & IN/OUT Ledger', icon: <HiOutlineArchive /> },
            { key: 'pos', label: 'Purchase Orders (PO)', icon: <HiOutlineShoppingCart /> },
            { key: 'quotations', label: 'Vendor Quotations', icon: <HiOutlineDocumentText /> },
            { key: 'vendors', label: 'Vendors Directory', icon: <HiOutlineOfficeBuilding /> },
            { key: 'gate_inward', label: 'Gate Inward & PO Tracking', icon: <HiOutlineTruck /> },
            { key: 'ocr', label: 'AI Bill Scanner', icon: <HiOutlineLightningBolt /> }
          ].map(tab => (
            <TabBtn key={tab.key} active={activeTab === tab.key} onClick={() => setActiveTab(tab.key)} icon={tab.icon}>
              {tab.label}
            </TabBtn>
          ))}
        </TabBar>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
          {activeTab === 'purchase_store' && (
            <>
              <button className="btn btn-primary btn-sm" onClick={() => handleOpenInwardModal()} style={{ background: '#10b981', borderColor: '#10b981' }}>
                <HiOutlineArrowDown /> Inward Stock
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => handleOpenOutwardModal()} style={{ color: '#f97316', borderColor: 'rgba(249,115,22,0.4)' }}>
                <HiOutlineArrowUp /> Issue Stock (Outward)
              </button>
            </>
          )}
          {activeTab === 'vendors' && (
            <button className="btn btn-primary btn-sm" onClick={() => handleOpenVendorModal()}>
              <HiOutlinePlus /> Add Vendor
            </button>
          )}
          {activeTab === 'quotations' && (
            <button className="btn btn-primary btn-sm" onClick={handleOpenQuotationModal}>
              <HiOutlinePlus /> Enter Quotation
            </button>
          )}
          {activeTab === 'pos' && (
            <button className="btn btn-primary btn-sm" onClick={handleOpenPOModal}>
              <HiOutlinePlus /> Issue PO
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={fetchData} title="Refresh Data">
            <HiOutlineRefresh /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-dim)' }}>
          <div className="spinner" style={{ margin: '0 auto 12px' }} /> Loading records...
        </div>
      ) : (
        <div>

          {/* ======================================================== */}
          {/* TAB: PURCHASE STORE & IN/OUT TRANSACTION LEDGER           */}
          {/* ======================================================== */}
          {activeTab === 'purchase_store' && (
            <div>

              {/* 1. Purchase Store Active Inventory Table */}
              <div className="glass-card" style={{ padding: 0, overflow: 'hidden', marginBottom: '24px' }}>
                <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>📦</span> Purchase Store Raw Material Inventory
                  </div>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-dim)' }}>
                    Live Stock currently stored in Purchase Store Warehouse
                  </span>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '850px' }}>
                  <thead>
                    <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid var(--border)' }}>
                      {['Material Name', 'Category / Type', 'Store Location / Rack', 'Current Stock', 'Total Inward', 'Total Issued', 'Status', 'Quick Actions'].map((h, i) => (
                        <th key={i} style={{ padding: '12px 16px', textAlign: 'left', color: 'var(--text-dim)', fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 700 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {storeInventory.length === 0 ? (
                      <tr><td colSpan={8} style={{ padding: '30px', textAlign: 'center', color: 'var(--text-dim)' }}>No materials in purchase store yet. Click "+ Inward Stock" to add your first batch.</td></tr>
                    ) : (
                      storeInventory.map(mat => {
                        const isLow = mat.currentStock <= (mat.minimumStock || 10);
                        return (
                          <tr key={mat._id} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={tdStyle}>
                              <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{mat.name}</div>
                              {mat.lastPOReference && <div style={{ fontSize: '0.72rem', color: '#3b82f6' }}>Last PO: {mat.lastPOReference}</div>}
                            </td>
                            <td style={tdStyle}>
                              <span style={{ textTransform: 'capitalize' }}>{mat.type || 'Nylon'}</span>
                            </td>
                            <td style={tdStyle}>
                              <span style={{ background: 'rgba(59,130,246,0.12)', color: '#3b82f6', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700 }}>
                                📍 {mat.storeLocation || 'Purchase Store'} {mat.rackNumber ? `(${mat.rackNumber})` : ''}
                              </span>
                            </td>
                            <td style={{ ...tdStyle, fontWeight: 800, color: isLow ? '#ef4444' : '#10b981', fontSize: '0.95rem' }}>
                              {mat.currentStock} {mat.unit}
                            </td>
                            <td style={{ ...tdStyle, color: '#10b981' }}>{mat.totalInward || 0} {mat.unit}</td>
                            <td style={{ ...tdStyle, color: '#f97316' }}>{mat.totalIssued || 0} {mat.unit}</td>
                            <td style={tdStyle}>
                              <span style={{
                                padding: '3px 8px', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 800,
                                background: isLow ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)',
                                color: isLow ? '#ef4444' : '#22c55e'
                              }}>
                                {isLow ? '⚠️ LOW BUFFER' : '✅ ADEQUATE'}
                              </span>
                            </td>
                            <td style={tdStyle}>
                              <div style={{ display: 'flex', gap: '6px' }}>
                                <button className="btn btn-secondary btn-sm" onClick={() => handleOpenInwardModal(mat)} title="Inward More Stock" style={{ color: '#10b981', padding: '3px 8px', fontSize: '0.75rem' }}>
                                  📥 Inward
                                </button>
                                <button className="btn btn-secondary btn-sm" onClick={() => handleOpenOutwardModal(mat)} title="Issue to Production" style={{ color: '#f97316', padding: '3px 8px', fontSize: '0.75rem' }}>
                                  📤 Issue
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* 2. Purchase Store IN / OUT Transaction Ledger */}
              <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <HiOutlineClipboardList style={{ fontSize: '1.2rem', color: 'var(--primary)' }} />
                    Purchase Store IN / OUT Stock Movement Ledger
                  </div>

                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {/* IN/OUT Filter Pills */}
                    <div style={{ display: 'flex', background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '2px', border: '1px solid var(--border)' }}>
                      {['ALL', 'IN', 'OUT'].map(f => (
                        <button
                          key={f}
                          onClick={() => setLedgerFilter(f)}
                          style={{
                            padding: '4px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                            fontSize: '0.75rem', fontWeight: 800,
                            background: ledgerFilter === f ? (f === 'IN' ? '#10b981' : f === 'OUT' ? '#ef4444' : 'var(--primary)') : 'transparent',
                            color: ledgerFilter === f ? '#fff' : 'var(--text-dim)'
                          }}
                        >
                          {f === 'IN' ? '🟢 IN ONLY' : f === 'OUT' ? '🔴 OUT ONLY' : 'ALL ENTRIES'}
                        </button>
                      ))}
                    </div>

                    <div style={{ position: 'relative', width: '220px' }}>
                      <HiOutlineSearch style={{ position: 'absolute', left: '10px', top: '9px', color: 'var(--text-dim)' }} />
                      <input
                        className="form-input"
                        style={{ paddingLeft: '30px', fontSize: '0.8rem', height: '32px' }}
                        placeholder="Search ref, PO, dept..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '950px' }}>
                  <thead>
                    <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid var(--border)' }}>
                      {['Date & Time', 'Movement Type', 'Material Name', 'Quantity', 'Ref / PO #', 'Vendor / Destination', 'Store Location', 'Balance After', 'Logged By'].map((h, i) => (
                        <th key={i} style={{ padding: '12px 16px', textAlign: 'left', color: 'var(--text-dim)', fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 700 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {storeLedger
                      .filter(l => (ledgerFilter === 'ALL' || l.entryType === ledgerFilter))
                      .filter(l => !searchQuery || (l.materialName?.toLowerCase().includes(searchQuery.toLowerCase()) || l.referenceNumber?.toLowerCase().includes(searchQuery.toLowerCase()) || l.destinationDepartment?.toLowerCase().includes(searchQuery.toLowerCase()) || l.vendorName?.toLowerCase().includes(searchQuery.toLowerCase())))
                      .length === 0 ? (
                      <tr><td colSpan={9} style={{ padding: '30px', textAlign: 'center', color: 'var(--text-dim)' }}>No store transactions matching criteria.</td></tr>
                    ) : (
                      storeLedger
                        .filter(l => (ledgerFilter === 'ALL' || l.entryType === ledgerFilter))
                        .filter(l => !searchQuery || (l.materialName?.toLowerCase().includes(searchQuery.toLowerCase()) || l.referenceNumber?.toLowerCase().includes(searchQuery.toLowerCase()) || l.destinationDepartment?.toLowerCase().includes(searchQuery.toLowerCase()) || l.vendorName?.toLowerCase().includes(searchQuery.toLowerCase())))
                        .map(entry => {
                          const isIN = entry.entryType === 'IN';
                          return (
                            <tr key={entry._id} style={{ borderBottom: '1px solid var(--border)' }}>
                              <td style={tdStyle}>
                                <div>{new Date(entry.transactionDate || entry.createdAt).toLocaleDateString()}</div>
                                <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)' }}>{new Date(entry.transactionDate || entry.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                              </td>
                              <td style={tdStyle}>
                                <span style={{
                                  padding: '3px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 900,
                                  background: isIN ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                                  color: isIN ? '#22c55e' : '#ef4444',
                                  display: 'inline-flex', alignItems: 'center', gap: '4px'
                                }}>
                                  {isIN ? '📥 IN (RECEIPT)' : '📤 OUT (ISSUE)'}
                                </span>
                              </td>
                              <td style={tdStyle}>
                                <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{entry.materialName}</div>
                                {entry.remarks && <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>{entry.remarks}</div>}
                              </td>
                              <td style={{ ...tdStyle, fontWeight: 800, color: isIN ? '#22c55e' : '#ef4444', fontSize: '0.9rem' }}>
                                {isIN ? '+' : '-'}{entry.quantity} {entry.unit}
                              </td>
                              <td style={tdStyle}>
                                <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#3b82f6', fontSize: '0.78rem' }}>
                                  {entry.referenceNumber || entry.poNumber || 'N/A'}
                                </span>
                              </td>
                              <td style={tdStyle}>
                                {isIN ? (
                                  <div>
                                    <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{entry.vendorName || 'Supplier'}</span>
                                    {entry.vendorCode && <span style={{ fontSize: '0.72rem', color: '#3b82f6', marginLeft: '4px' }}>({entry.vendorCode})</span>}
                                  </div>
                                ) : (
                                  <div>
                                    <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{entry.destinationDepartment || 'Production Line'}</span>
                                    {entry.machineNumber && <span style={{ fontSize: '0.72rem', color: '#f97316', marginLeft: '4px' }}>({entry.machineNumber})</span>}
                                  </div>
                                )}
                              </td>
                              <td style={tdStyle}>
                                <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>
                                  {entry.storeLocation} {entry.rackBin ? `(${entry.rackBin})` : ''}
                                </span>
                              </td>
                              <td style={{ ...tdStyle, fontWeight: 800, color: 'var(--text-primary)' }}>
                                {entry.balanceAfter} {entry.unit}
                              </td>
                              <td style={tdStyle}>
                                <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                                  👤 {entry.performedByName || 'Purchase User'}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                    )}
                  </tbody>
                </table>
              </div>

            </div>
          )}

          {/* ======================================================== */}
          {/* TAB 2: PURCHASE ORDERS (PO)                              */}
          {/* ======================================================== */}
          {activeTab === 'pos' && (
            <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>🛒</span> Official Purchase Orders (Auto-Numbered PO-YYYY-XXXX)
                </div>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-dim)' }}>
                  POs are linked with Factory Gate Entry & Purchase Store Inward.
                </span>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '950px' }}>
                <thead>
                  <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid var(--border)' }}>
                    {['PO Number', 'Vendor / Supplier', 'Order Date', 'Materials & Quantities', 'Inward Progress', 'Grand Total', 'Status', 'Actions'].map((h, i) => (
                      <th key={i} style={{ padding: '12px 16px', textAlign: 'left', color: 'var(--text-dim)', fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 700 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pos.length === 0 ? (
                    <tr><td colSpan={8} style={{ padding: '30px', textAlign: 'center', color: 'var(--text-dim)' }}>No Purchase Orders issued yet. Click "+ Issue PO".</td></tr>
                  ) : (
                    pos.map(po => {
                      const totalOrdered = (po.items || []).reduce((acc, it) => acc + (Number(it.quantity) || 0), 0);
                      const received = po.totalReceivedQty || 0;
                      const percent = totalOrdered > 0 ? Math.min(100, Math.round((received / totalOrdered) * 100)) : 0;

                      return (
                        <tr key={po._id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={tdStyle}>
                            <div style={{ fontWeight: 800, color: '#3b82f6', fontSize: '0.9rem' }}>{po.poNumber}</div>
                            {po.quotationNumber && <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)' }}>Ref: {po.quotationNumber}</div>}
                          </td>
                          <td style={tdStyle}>
                            <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{po.vendorName || po.vendorId?.vendorName}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>{po.vendorCode || po.vendorId?.vendorCode}</div>
                          </td>
                          <td style={tdStyle}>
                            <div>{new Date(po.orderDate || po.createdAt).toLocaleDateString()}</div>
                            {po.expectedDeliveryDate && <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>Due: {new Date(po.expectedDeliveryDate).toLocaleDateString()}</div>}
                          </td>
                          <td style={tdStyle}>
                            <div style={{ fontSize: '0.8rem' }}>
                              {(po.items || []).map((it, idx) => (
                                <div key={idx}>• {it.materialType}: {it.quantity} {it.unit} @ ₹{it.rate}</div>
                              ))}
                            </div>
                          </td>
                          <td style={{ ...tdStyle, width: '150px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', marginBottom: '2px' }}>
                              <span>{received} / {totalOrdered} kg</span>
                              <span style={{ fontWeight: 700 }}>{percent}%</span>
                            </div>
                            <div style={{ height: '6px', background: 'rgba(255,255,255,0.08)', borderRadius: '3px', overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${percent}%`, background: percent === 100 ? '#10b981' : '#3b82f6', borderRadius: '3px' }} />
                            </div>
                          </td>
                          <td style={{ ...tdStyle, fontWeight: 800, color: '#10b981', fontSize: '0.95rem' }}>
                            ₹{(po.grandTotal || po.totalAmount || 0).toLocaleString()}
                          </td>
                          <td style={tdStyle}>
                            <span style={{
                              padding: '3px 8px', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 800,
                              background: po.status === 'received' ? 'rgba(34,197,94,0.15)' : po.status === 'partially_received' ? 'rgba(245,158,11,0.15)' : 'rgba(59,130,246,0.15)',
                              color: po.status === 'received' ? '#22c55e' : po.status === 'partially_received' ? '#f59e0b' : '#3b82f6'
                            }}>
                              {po.status?.toUpperCase()}
                            </span>
                          </td>
                          <td style={tdStyle}>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => handleDownloadPOPDF(po)}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem', padding: '4px 8px' }}
                              >
                                <HiOutlineDownload /> PDF
                              </button>
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => handleOpenInwardModal({
                                  materialName: po.items?.[0]?.materialType || 'Nylon Granules',
                                  quantity: totalOrdered - received > 0 ? totalOrdered - received : totalOrdered,
                                  poNumber: po.poNumber,
                                  vendorName: po.vendorName
                                })}
                                style={{ fontSize: '0.75rem', padding: '4px 8px', background: '#10b981', borderColor: '#10b981' }}
                                title="Inward directly to Purchase Store"
                              >
                                📥 Inward
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ======================================================== */}
          {/* TAB 3: VENDOR QUOTATIONS                                 */}
          {/* ======================================================== */}
          {activeTab === 'quotations' && (
            <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>📑</span> Vendor Quotation Management & Comparison
                </div>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-dim)' }}>
                  Approve quotation to convert directly into an official Purchase Order (PO).
                </span>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                <thead>
                  <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid var(--border)' }}>
                    {['Quotation #', 'Vendor', 'Date / Valid Until', 'Items & Materials', 'SubTotal', 'GST', 'Grand Total', 'Status', 'Actions'].map((h, i) => (
                      <th key={i} style={{ padding: '12px 16px', textAlign: 'left', color: 'var(--text-dim)', fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 700 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {quotations.length === 0 ? (
                    <tr><td colSpan={9} style={{ padding: '30px', textAlign: 'center', color: 'var(--text-dim)' }}>No quotations entered yet. Click "+ Enter Quotation".</td></tr>
                  ) : (
                    quotations.map(q => {
                      const isConverted = q.status === 'converted_to_po';
                      return (
                        <tr key={q._id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={tdStyle}>
                            <span style={{ fontWeight: 800, color: 'var(--primary)' }}>{q.quotationNumber}</span>
                          </td>
                          <td style={tdStyle}>
                            <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{q.vendorName || q.vendorId?.vendorName}</div>
                            <div style={{ fontSize: '0.75rem', color: '#3b82f6' }}>{q.vendorCode || q.vendorId?.vendorCode}</div>
                          </td>
                          <td style={tdStyle}>
                            <div>{new Date(q.quotationDate).toLocaleDateString()}</div>
                            {q.validUntil && <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>Valid till {new Date(q.validUntil).toLocaleDateString()}</div>}
                          </td>
                          <td style={tdStyle}>
                            <div style={{ fontSize: '0.8rem' }}>
                              {(q.items || []).map((it, idx) => (
                                <div key={idx}>• {it.materialType}: {it.quantity} {it.unit} @ ₹{it.unitRate}</div>
                              ))}
                            </div>
                          </td>
                          <td style={tdStyle}>₹{(q.subTotal || 0).toLocaleString()}</td>
                          <td style={tdStyle}>₹{(q.taxAmount || 0).toLocaleString()}</td>
                          <td style={{ ...tdStyle, fontWeight: 800, color: '#10b981', fontSize: '0.95rem' }}>
                            ₹{(q.grandTotal || 0).toLocaleString()}
                          </td>
                          <td style={tdStyle}>
                            <span style={{
                              padding: '3px 8px', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 800,
                              background: isConverted ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)',
                              color: isConverted ? '#22c55e' : '#f59e0b'
                            }}>
                              {isConverted ? `CONVERTED (PO: ${q.poNumber || ''})` : q.status?.toUpperCase()}
                            </span>
                          </td>
                          <td style={tdStyle}>
                            {!isConverted ? (
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => handleConvertToPO(q)}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem', padding: '4px 8px' }}
                              >
                                ⚡ Generate PO
                              </button>
                            ) : (
                              <span style={{ fontSize: '0.75rem', color: '#22c55e', fontWeight: 700 }}>✅ PO Active</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ======================================================== */}
          {/* TAB 4: VENDORS MASTER & DIRECTORY                        */}
          {/* ======================================================== */}
          {activeTab === 'vendors' && (
            <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>🏭</span> Registered Vendors & Suppliers Master
                </div>
                <div style={{ position: 'relative', width: '250px' }}>
                  <HiOutlineSearch style={{ position: 'absolute', left: '10px', top: '10px', color: 'var(--text-dim)' }} />
                  <input
                    className="form-input"
                    style={{ paddingLeft: '32px', fontSize: '0.8rem', height: '34px' }}
                    placeholder="Search by Code, Name, GST..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '850px' }}>
                <thead>
                  <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid var(--border)' }}>
                    {['Vendor Code', 'Vendor Name', 'Contact & Phone', 'GSTIN', 'Materials Supplied', 'Payment Terms', 'Rating', 'Actions'].map((h, i) => (
                      <th key={i} style={{ padding: '12px 16px', textAlign: 'left', color: 'var(--text-dim)', fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 700 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {vendors
                    .filter(v => !searchQuery || (v.vendorName?.toLowerCase().includes(searchQuery.toLowerCase()) || v.vendorCode?.toLowerCase().includes(searchQuery.toLowerCase()) || v.gstNumber?.toLowerCase().includes(searchQuery.toLowerCase())))
                    .length === 0 ? (
                    <tr><td colSpan={8} style={{ padding: '30px', textAlign: 'center', color: 'var(--text-dim)' }}>No vendors found. Click "+ Add Vendor" to create one.</td></tr>
                  ) : (
                    vendors
                      .filter(v => !searchQuery || (v.vendorName?.toLowerCase().includes(searchQuery.toLowerCase()) || v.vendorCode?.toLowerCase().includes(searchQuery.toLowerCase()) || v.gstNumber?.toLowerCase().includes(searchQuery.toLowerCase())))
                      .map(v => (
                        <tr key={v._id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={tdStyle}>
                            <span style={{ background: 'rgba(59,130,246,0.15)', color: '#3b82f6', padding: '3px 8px', borderRadius: '6px', fontWeight: 800, fontSize: '0.8rem' }}>
                              {v.vendorCode || 'VEN-NEW'}
                            </span>
                          </td>
                          <td style={tdStyle}>
                            <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{v.vendorName}</div>
                            {v.city && <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>📍 {v.city}, {v.state}</div>}
                          </td>
                          <td style={tdStyle}>
                            <div>{v.contactPerson || 'N/A'}</div>
                            {v.phone && <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>📞 {v.phone}</div>}
                          </td>
                          <td style={tdStyle}>
                            <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{v.gstNumber || 'N/A'}</span>
                          </td>
                          <td style={tdStyle}>{v.materialSupplied || 'Nylon Granules'}</td>
                          <td style={tdStyle}>
                            <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{v.paymentTerms || '30 Days Net'}</span>
                          </td>
                          <td style={tdStyle}>
                            <span style={{ color: '#f59e0b', fontWeight: 700 }}>{'⭐'.repeat(v.rating || 5)}</span>
                          </td>
                          <td style={tdStyle}>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button className="btn btn-secondary btn-sm" onClick={() => handleOpenVendorModal(v)} title="Edit Vendor">
                                <HiOutlinePencil />
                              </button>
                              <button className="btn btn-secondary btn-sm" onClick={() => handleDeleteVendor(v._id)} title="Delete Vendor" style={{ color: '#ef4444' }}>
                                <HiOutlineTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ======================================================== */}
          {/* TAB 5: GATE INWARD & PO RECONCILIATION                   */}
          {/* ======================================================== */}
          {activeTab === 'gate_inward' && (
            <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>🚚</span> Inward Gate Entries & PO Approval Reconciliation
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginTop: '2px' }}>
                    Gate Guards match material arrivals against open POs before entry approval.
                  </div>
                </div>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                <thead>
                  <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid var(--border)' }}>
                    {['Entry Date', 'Linked PO #', 'Vendor Name', 'Bill / Challan #', 'Vehicle #', 'Inward Quantity', 'Gate Status', 'GRN #', 'Action'].map((h, i) => (
                      <th key={i} style={{ padding: '12px 16px', textAlign: 'left', color: 'var(--text-dim)', fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 700 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {gateEntries.length === 0 ? (
                    <tr><td colSpan={9} style={{ padding: '30px', textAlign: 'center', color: 'var(--text-dim)' }}>No inward gate entries recorded yet.</td></tr>
                  ) : (
                    gateEntries.map(ge => (
                      <tr key={ge._id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={tdStyle}>{new Date(ge.createdAt || ge.billDate).toLocaleDateString()}</td>
                        <td style={tdStyle}>
                          {ge.poNumber ? (
                            <span style={{ background: 'rgba(59,130,246,0.15)', color: '#3b82f6', padding: '2px 6px', borderRadius: '4px', fontWeight: 800, fontSize: '0.75rem' }}>
                              {ge.poNumber}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }}>Direct Inward</span>
                          )}
                        </td>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 600 }}>{ge.vendorName}</div>
                          {ge.vendorCode && <div style={{ fontSize: '0.72rem', color: '#3b82f6' }}>{ge.vendorCode}</div>}
                        </td>
                        <td style={tdStyle}>{ge.billNumber || 'N/A'}</td>
                        <td style={tdStyle}>{ge.vehicleNumber || 'N/A'}</td>
                        <td style={{ ...tdStyle, fontWeight: 700, color: 'var(--text-primary)' }}>
                          {ge.quantity} {ge.unit || 'kg'}
                        </td>
                        <td style={tdStyle}>
                          <span style={{
                            padding: '3px 8px', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 800,
                            background: ge.status === 'grn_created' ? 'rgba(34,197,94,0.15)' : ge.status === 'verified' ? 'rgba(59,130,246,0.15)' : 'rgba(245,158,11,0.15)',
                            color: ge.status === 'grn_created' ? '#22c55e' : ge.status === 'verified' ? '#3b82f6' : '#f59e0b'
                          }}>
                            {ge.status === 'grn_created' ? 'APPROVED & GRN' : ge.status?.toUpperCase()}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#10b981' }}>{ge.grnNumber || '-'}</span>
                        </td>
                        <td style={tdStyle}>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => handleOpenInwardModal({
                              materialName: ge.materialType || 'Nylon Granules',
                              quantity: ge.quantity,
                              unit: ge.unit,
                              poNumber: ge.poNumber,
                              vendorName: ge.vendorName,
                              billNumber: ge.billNumber
                            })}
                            style={{ fontSize: '0.72rem', padding: '3px 8px', background: '#10b981', borderColor: '#10b981' }}
                            title="Inward to Purchase Store"
                          >
                            📥 Inward to Store
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ======================================================== */}
          {/* TAB 6: AI INVOICE SCANNER (OCR)                          */}
          {/* ======================================================== */}
          {activeTab === 'ocr' && (
            <div>
              <div className="glass-card" style={{ marginBottom: '24px', background: 'linear-gradient(145deg, rgba(59,130,246,0.05), rgba(168,85,247,0.05))', border: '1px solid rgba(59,130,246,0.2)' }}>
                <h3 style={{ color: 'var(--text-primary)', fontSize: '1.2rem', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <HiOutlineLightningBolt style={{ color: '#a855f7' }} /> AI Auto-Invoice Entry (OCR)
                </h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '20px' }}>
                  Upload a scanned vendor bill. Our AI will automatically extract the Vendor Name, Invoice Number, and Total Amount to create an ERP entry.
                </p>
                
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                  <input type="file" className="form-input" style={{ width: '300px' }} accept="image/*,.pdf" onChange={e => setSelectedFile(e.target.files[0])} />
                  <button className="btn btn-primary" disabled={!selectedFile || scanning} onClick={handleScanInvoice} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <HiOutlineLightningBolt /> {scanning ? 'Scanning via AI...' : 'Extract Data & Save'}
                  </button>
                </div>
              </div>

              <div className="glass-card" style={{ padding: '0' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                  <thead>
                    <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      {['Invoice No', 'Vendor Name', 'Total Amount', 'Tax', 'Extracted Via', 'View', 'Status'].map((h, i) => (
                        <th key={i} style={{ padding: '14px 20px', textAlign: 'left', color: 'var(--text-dim)', fontSize: '0.8rem', textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.length === 0 ? <tr><td colSpan={7} style={{ padding: '20px', textAlign: 'center' }}>No invoices scanned yet.</td></tr> : invoices.map(inv => (
                      <tr key={inv._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                        <td style={tdStyle}><span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{inv.invoiceNumber}</span></td>
                        <td style={tdStyle}>{inv.vendorName}</td>
                        <td style={{ ...tdStyle, color: '#3b82f6', fontWeight: 600 }}>₹{inv.totalAmount}</td>
                        <td style={tdStyle}>₹{inv.taxAmount}</td>
                        <td style={tdStyle}>
                          <span style={{ fontSize: '0.75rem', background: 'rgba(168,85,247,0.1)', color: '#c084fc', padding: '4px 8px', borderRadius: '4px' }}>
                            {inv.rawOcrText && (inv.rawOcrText.includes('OpenAI') || inv.rawOcrText.includes('GPT')) ? 'OpenAI GPT' : 'Fallback Engine'}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <button 
                            className="btn btn-secondary btn-sm" 
                            onClick={() => setSelectedInvoiceForView(inv)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 8px', fontSize: '0.75rem', height: '28px' }}
                          >
                            <HiOutlineEye /> View
                          </button>
                        </td>
                        <td style={tdStyle}><span className="status-badge verified">{inv.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL: PLACE INWARD ENTRY INTO PURCHASE STORE            */}
      {/* ======================================================== */}
      {showInwardModal && (
        <ModalOverlay onClose={() => setShowInwardModal(false)} title="📥 Place Inward Entry (Purchase Store)" maxWidth="600px">
          <form onSubmit={handleSaveInward}>
            <div style={{
              background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.25)',
              borderRadius: '10px', padding: '12px', marginBottom: '16px', fontSize: '0.8rem', color: 'var(--text-secondary)'
            }}>
              Deposits raw material into <strong>Purchase Store Inventory</strong> and creates a permanent <strong>🟢 IN Ledger Record</strong> with running balance.
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Material Name *</label>
                <input className="form-input" required placeholder="e.g. Nylon 66 Granules" value={inwardForm.materialName} onChange={e => setInwardForm(p => ({ ...p, materialName: e.target.value }))} />
              </div>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Material Type</label>
                <select className="form-input" value={inwardForm.materialType} onChange={e => setInwardForm(p => ({ ...p, materialType: e.target.value }))}>
                  <option value="nylon">Nylon</option>
                  <option value="pigment">Pigment</option>
                  <option value="hardware">Hardware / Pins</option>
                  <option value="packaging">Packaging</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Inward Quantity *</label>
                <input className="form-input" type="number" step="any" min="0.01" required value={inwardForm.quantity} onChange={e => setInwardForm(p => ({ ...p, quantity: e.target.value }))} />
              </div>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Unit</label>
                <select className="form-input" value={inwardForm.unit} onChange={e => setInwardForm(p => ({ ...p, unit: e.target.value }))}>
                  <option value="kg">KG</option>
                  <option value="pcs">PCS</option>
                  <option value="bags">Bags</option>
                  <option value="meters">Meters</option>
                  <option value="liters">Liters</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Purchase Store Location</label>
                <input className="form-input" value={inwardForm.storeLocation} onChange={e => setInwardForm(p => ({ ...p, storeLocation: e.target.value }))} placeholder="Purchase Store - Bay A1" />
              </div>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Rack / Bin Number</label>
                <input className="form-input" value={inwardForm.rackBin} onChange={e => setInwardForm(p => ({ ...p, rackBin: e.target.value }))} placeholder="Rack A-1 / Bin 02" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>PO Number (Optional)</label>
                <input className="form-input" placeholder="PO-2026-0001" value={inwardForm.poNumber} onChange={e => setInwardForm(p => ({ ...p, poNumber: e.target.value }))} />
              </div>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Vendor Name (Optional)</label>
                <input className="form-input" placeholder="Supplier Name" value={inwardForm.vendorName} onChange={e => setInwardForm(p => ({ ...p, vendorName: e.target.value }))} />
              </div>
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label className="form-label" style={{ fontSize: '0.78rem' }}>Bill / Delivery Challan Ref #</label>
              <input className="form-input" placeholder="INV-8890 / DC-101" value={inwardForm.billNumber} onChange={e => setInwardForm(p => ({ ...p, billNumber: e.target.value }))} />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label className="form-label" style={{ fontSize: '0.78rem' }}>Remarks / Storage Notes</label>
              <input className="form-input" placeholder="e.g. Batch #NY-88, inspected and verified" value={inwardForm.remarks} onChange={e => setInwardForm(p => ({ ...p, remarks: e.target.value }))} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowInwardModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" style={{ background: '#10b981', borderColor: '#10b981' }}>
                📥 Record Inward & Update Store
              </button>
            </div>
          </form>
        </ModalOverlay>
      )}

      {/* ======================================================== */}
      {/* MODAL: PLACE OUTWARD / ISSUE STOCK ENTRY                 */}
      {/* ======================================================== */}
      {showOutwardModal && (
        <ModalOverlay onClose={() => setShowOutwardModal(false)} title="📤 Issue Stock Outward (To Production)" maxWidth="600px">
          <form onSubmit={handleSaveOutward}>
            <div style={{
              background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.25)',
              borderRadius: '10px', padding: '12px', marginBottom: '16px', fontSize: '0.8rem', color: 'var(--text-secondary)'
            }}>
              Deducts raw material from <strong>Purchase Store</strong> for production and creates a permanent <strong>🔴 OUT Ledger Record</strong> with remaining balance.
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ fontSize: '0.78rem' }}>Select Raw Material to Issue *</label>
              <select
                className="form-input"
                required
                value={outwardForm.materialId}
                onChange={e => {
                  const id = e.target.value;
                  const selected = storeInventory.find(m => m._id === id);
                  if (selected) {
                    setOutwardForm(p => ({
                      ...p,
                      materialId: selected._id,
                      materialName: selected.name,
                      unit: selected.unit,
                      quantity: Math.min(p.quantity || 10, selected.currentStock)
                    }));
                  }
                }}
              >
                <option value="">-- Choose Material from Store --</option>
                {storeInventory.map(m => (
                  <option key={m._id} value={m._id}>
                    {m.name} — Available: {m.currentStock} {m.unit} (📍 {m.storeLocation || 'Main Bay'})
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Issue Quantity *</label>
                <input className="form-input" type="number" step="any" min="0.01" required value={outwardForm.quantity} onChange={e => setOutwardForm(p => ({ ...p, quantity: e.target.value }))} />
              </div>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Unit</label>
                <input className="form-input" readOnly disabled value={outwardForm.unit} style={{ background: 'rgba(0,0,0,0.06)' }} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Destination Department *</label>
                <select className="form-input" value={outwardForm.destinationDepartment} onChange={e => setOutwardForm(p => ({ ...p, destinationDepartment: e.target.value }))}>
                  <option value="Molding Line 1">Molding Line 1</option>
                  <option value="Molding Line 2">Molding Line 2</option>
                  <option value="Extrusion Dept">Extrusion Dept</option>
                  <option value="Assembly Line">Assembly Line</option>
                  <option value="Testing & Quality Lab">Testing & Quality Lab</option>
                  <option value="Job Work Contractor">Job Work Contractor</option>
                  <option value="Vendor Return (Damaged)">Vendor Return (Damaged)</option>
                </select>
              </div>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Machine / Workstation No</label>
                <input className="form-input" placeholder="e.g. M-01, M-02" value={outwardForm.machineNumber} onChange={e => setOutwardForm(p => ({ ...p, machineNumber: e.target.value }))} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Issued To Person</label>
                <input className="form-input" placeholder="Operator / Supervisor Name" value={outwardForm.issuedToPerson} onChange={e => setOutwardForm(p => ({ ...p, issuedToPerson: e.target.value }))} />
              </div>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Issue Slip # (Optional)</label>
                <input className="form-input" placeholder="ISSUE-001" value={outwardForm.issueSlipNumber} onChange={e => setOutwardForm(p => ({ ...p, issueSlipNumber: e.target.value }))} />
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label className="form-label" style={{ fontSize: '0.78rem' }}>Remarks / Job Order Reference</label>
              <input className="form-input" placeholder="e.g. Issued for Drag Chain Model 35.1 batch" value={outwardForm.remarks} onChange={e => setOutwardForm(p => ({ ...p, remarks: e.target.value }))} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowOutwardModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" style={{ background: '#ef4444', borderColor: '#ef4444' }}>
                📤 Issue Stock & Update Ledger
              </button>
            </div>
          </form>
        </ModalOverlay>
      )}

      {/* ======================================================== */}
      {/* MODAL 1: ADD / EDIT VENDOR                               */}
      {/* ======================================================== */}
      {showVendorModal && (
        <ModalOverlay onClose={() => setShowVendorModal(false)} title={editingVendorId ? "Edit Vendor Details" : "Add New Supplier / Vendor"}>
          <form onSubmit={handleSaveVendor}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Vendor Code</label>
                <input className="form-input" placeholder="Auto (VEN-XXXX)" value={vendorForm.vendorCode} onChange={e => setVendorForm(p => ({ ...p, vendorCode: e.target.value }))} />
              </div>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Vendor / Company Name *</label>
                <input className="form-input" required placeholder="e.g. Apex Polymer Industries" value={vendorForm.vendorName} onChange={e => setVendorForm(p => ({ ...p, vendorName: e.target.value }))} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Contact Person</label>
                <input className="form-input" placeholder="Mr. Rajesh Kumar" value={vendorForm.contactPerson} onChange={e => setVendorForm(p => ({ ...p, contactPerson: e.target.value }))} />
              </div>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Phone / Mobile *</label>
                <input className="form-input" placeholder="+91 9876543210" value={vendorForm.phone} onChange={e => setVendorForm(p => ({ ...p, phone: e.target.value }))} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Email Address</label>
                <input className="form-input" type="email" placeholder="sales@vendor.com" value={vendorForm.email} onChange={e => setVendorForm(p => ({ ...p, email: e.target.value }))} />
              </div>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>GSTIN Number</label>
                <input className="form-input" placeholder="27AAPCA1234F1Z5" value={vendorForm.gstNumber} onChange={e => setVendorForm(p => ({ ...p, gstNumber: e.target.value }))} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Material Supplied</label>
                <input className="form-input" placeholder="e.g. Nylon 66 Granules, Metal Pins" value={vendorForm.materialSupplied} onChange={e => setVendorForm(p => ({ ...p, materialSupplied: e.target.value }))} />
              </div>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Payment Terms</label>
                <select className="form-input" value={vendorForm.paymentTerms} onChange={e => setVendorForm(p => ({ ...p, paymentTerms: e.target.value }))}>
                  <option value="Immediate">Immediate / Advance</option>
                  <option value="15 Days Net">15 Days Net</option>
                  <option value="30 Days Net">30 Days Net</option>
                  <option value="45 Days Net">45 Days Net</option>
                  <option value="60 Days Net">60 Days Net</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ fontSize: '0.78rem' }}>Address & City</label>
              <input className="form-input" placeholder="Factory / Office Street Address, City, State" value={vendorForm.address} onChange={e => setVendorForm(p => ({ ...p, address: e.target.value }))} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowVendorModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary">{editingVendorId ? "Update Vendor" : "Save Vendor"}</button>
            </div>
          </form>
        </ModalOverlay>
      )}

      {/* ======================================================== */}
      {/* MODAL 2: ENTER VENDOR QUOTATION                          */}
      {/* ======================================================== */}
      {showQuotationModal && (
        <ModalOverlay onClose={() => setShowQuotationModal(false)} title="Enter Vendor Quotation" maxWidth="650px">
          <form onSubmit={handleSaveQuotation}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Quotation Ref # *</label>
                <input className="form-input" required placeholder="e.g. VQ-2026-001" value={quotationForm.quotationNumber} onChange={e => setQuotationForm(p => ({ ...p, quotationNumber: e.target.value }))} />
              </div>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Select Vendor *</label>
                <select className="form-input" required value={quotationForm.vendorId} onChange={e => setQuotationForm(p => ({ ...p, vendorId: e.target.value }))}>
                  <option value="">-- Choose Vendor --</option>
                  {vendors.map(v => <option key={v._id} value={v._id}>{v.vendorName} ({v.vendorCode})</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Quotation Date</label>
                <input className="form-input" type="date" value={quotationForm.quotationDate} onChange={e => setQuotationForm(p => ({ ...p, quotationDate: e.target.value }))} />
              </div>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Valid Until</label>
                <input className="form-input" type="date" value={quotationForm.validUntil} onChange={e => setQuotationForm(p => ({ ...p, validUntil: e.target.value }))} />
              </div>
            </div>

            {/* Itemized Materials Table */}
            <div style={{ marginBottom: '16px', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>📦 Quoted Materials & Unit Rates</span>
                <button type="button" className="btn btn-secondary btn-sm" onClick={handleAddQuotationItem} style={{ fontSize: '0.75rem', padding: '2px 8px' }}>
                  + Add Item
                </button>
              </div>

              {quotationForm.items.map((item, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 24px', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                  <input className="form-input" style={{ fontSize: '0.8rem' }} placeholder="Material Type / Name" value={item.materialType} onChange={e => handleQuotationItemChange(idx, 'materialType', e.target.value)} />
                  <input className="form-input" style={{ fontSize: '0.8rem' }} type="number" placeholder="Qty" value={item.quantity} onChange={e => handleQuotationItemChange(idx, 'quantity', Number(e.target.value))} />
                  <input className="form-input" style={{ fontSize: '0.8rem' }} type="number" placeholder="Rate (₹)" value={item.unitRate} onChange={e => handleQuotationItemChange(idx, 'unitRate', Number(e.target.value))} />
                  <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#10b981' }}>
                    ₹{((item.quantity || 0) * (item.unitRate || 0)).toLocaleString()}
                  </div>
                  {quotationForm.items.length > 1 && (
                    <button type="button" onClick={() => handleRemoveQuotationItem(idx)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}>×</button>
                  )}
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowQuotationModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary">Save Quotation</button>
            </div>
          </form>
        </ModalOverlay>
      )}

      {/* ======================================================== */}
      {/* MODAL 3: CREATE PURCHASE ORDER (PO)                      */}
      {/* ======================================================== */}
      {showPOModal && (
        <ModalOverlay onClose={() => setShowPOModal(false)} title="Issue Official Purchase Order (PO)" maxWidth="650px">
          <form onSubmit={handleSavePO}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <label className="form-label" style={{ fontSize: '0.78rem', margin: 0 }}>PO Number</label>
                  <span style={{ fontSize: '0.68rem', color: '#10b981', fontWeight: 700 }}>✨ Auto-Generated</span>
                </div>
                <input className="form-input" placeholder="PO-2026-XXXX" value={poForm.poNumber} onChange={e => setPoForm(p => ({ ...p, poNumber: e.target.value }))} />
              </div>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Select Vendor / Supplier *</label>
                <select className="form-input" required value={poForm.vendorId} onChange={e => setPoForm(p => ({ ...p, vendorId: e.target.value }))}>
                  <option value="">-- Choose Vendor --</option>
                  {vendors.map(v => <option key={v._id} value={v._id}>{v.vendorName} ({v.vendorCode})</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>PO Order Date</label>
                <input className="form-input" type="date" value={poForm.orderDate} onChange={e => setPoForm(p => ({ ...p, orderDate: e.target.value }))} />
              </div>
              <div>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Expected Delivery Date</label>
                <input className="form-input" type="date" value={poForm.expectedDeliveryDate} onChange={e => setPoForm(p => ({ ...p, expectedDeliveryDate: e.target.value }))} />
              </div>
            </div>

            {/* PO Line Items */}
            <div style={{ marginBottom: '16px', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>📦 PO Line Items</span>
                <button type="button" className="btn btn-secondary btn-sm" onClick={handleAddPOItem} style={{ fontSize: '0.75rem', padding: '2px 8px' }}>
                  + Add Item
                </button>
              </div>

              {poForm.items.map((item, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 24px', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                  <input className="form-input" style={{ fontSize: '0.8rem' }} placeholder="Material Type" value={item.materialType} onChange={e => handlePOItemChange(idx, 'materialType', e.target.value)} />
                  <input className="form-input" style={{ fontSize: '0.8rem' }} type="number" placeholder="Qty" value={item.quantity} onChange={e => handlePOItemChange(idx, 'quantity', Number(e.target.value))} />
                  <input className="form-input" style={{ fontSize: '0.8rem' }} type="number" placeholder="Rate (₹)" value={item.rate} onChange={e => handlePOItemChange(idx, 'rate', Number(e.target.value))} />
                  <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#3b82f6' }}>
                    ₹{((item.quantity || 0) * (item.rate || 0)).toLocaleString()}
                  </div>
                  {poForm.items.length > 1 && (
                    <button type="button" onClick={() => handleRemovePOItem(idx)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}>×</button>
                  )}
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowPOModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary">Generate & Issue PO</button>
            </div>
          </form>
        </ModalOverlay>
      )}

      {/* VIEW SCAN MODAL */}
      {selectedInvoiceForView && (
        <ModalOverlay onClose={() => setSelectedInvoiceForView(null)} title={`Invoice Details: ${selectedInvoiceForView.invoiceNumber}`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Vendor</span>
                <div style={{ fontWeight: 700 }}>{selectedInvoiceForView.vendorName}</div>
              </div>
              <div>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Invoice #</span>
                <div style={{ fontWeight: 700 }}>{selectedInvoiceForView.invoiceNumber}</div>
              </div>
              <div>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Total Amount</span>
                <div style={{ fontWeight: 800, color: '#10b981' }}>₹{selectedInvoiceForView.totalAmount}</div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setSelectedInvoiceForView(null)}>Close</button>
            </div>
          </div>
        </ModalOverlay>
      )}

    </DashboardLayout>
  );
};

const tdStyle = { padding: '12px 16px', color: 'var(--text-secondary)', verticalAlign: 'middle', fontSize: '0.84rem' };

const ModalOverlay = ({ children, onClose, title, maxWidth = '500px' }) => (
  <div onClick={(e) => e.target === e.currentTarget && onClose()} style={{
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
  }}>
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px',
      width: '100%', maxWidth, maxHeight: '90vh', display: 'flex', flexDirection: 'column',
      overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.5)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>{title}</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', fontSize: '1.3rem', cursor: 'pointer' }}><HiOutlineX /></button>
      </div>
      <div style={{ padding: '24px', overflowY: 'auto', overflowX: 'hidden', flex: 1 }}>
        {children}
      </div>
    </div>
  </div>
);

export default FinanceDashboard;
